import forge from 'apis/forge';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { MenuItem, MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { showContextMenu } from 'redux/mode/actions';
import { displayConfirmModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { useMobileTapBehavior } from 'hooks/useMobileTapBehavior';
import { useNavigate } from 'react-router';
import * as routes from 'routes';
import { trackDesignSystemAction } from 'analytics';
import { useState } from 'react';
import ShareDesignSystemModal from 'components/Modal/Share/ShareDesignSystemModal';
import * as S from '../styles';

interface Props {
	designSystem: DesignSystem;
	isActive?: boolean;
	onClick?: () => void;
	onCommandClick?: () => void;
}

const DesignSystemListItem = ({ designSystem, isActive, onClick, onCommandClick }: Props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [showShareModal, setShowShareModal] = useState(false);
	const { isMobile, activeDesignSystemId } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			activeDesignSystemId: r.mode.activeDesignSystemId,
		}),
		shallowEqual
	);

	const { isPressed, touchHandlers } = useMobileTapBehavior(isMobile);

	const menuItems: MenuItem[] = [
		new MenuItemAction('Open design system', '', () => {
			onClick && onClick();
		}),
		new MenuItemAction('Open design system in new tab', '', () => {
			window.open(
				routes.designSystem(designSystem.id, designSystem.title),
				'_blank',
				'noopener,noreferrer'
			);
		}),
		new MenuItemDivider(),
		MenuItemAction.createRenameDesignSystem(designSystem),
		new MenuItemDivider(),
		new MenuItemAction('Share...', '', () => {
			trackDesignSystemAction('open_share_modal');
			setShowShareModal(true);
		}),
		new MenuItemAction('Delete design system...', '', async () => {
			const users = await forge.getDesignSystemUsers({ designSystemId: designSystem.id });

			if (users.emails.length === 1) {
				displayConfirmModal({
					title: 'Delete design system',
					description: 'Are you sure you want to delete this design system?',
					submitLabel: 'Delete',
					onCancel: () => {},
					onSubmit: async () => {
						await forge.deleteDesignSystem(
							{
								designSystemId: designSystem.id,
							},
							true
						);
						// design system is gone! take user to the home page
						if (designSystem.id === activeDesignSystemId)
							navigate(routes.home(), { replace: true });
					},
				});
			} else {
				const baselineMessage = `${users.emails.length - 1} other user${
					users.emails.length - 1 === 1 ? ' has' : 's have'
				} access to this design system. If you delete it, they will no longer have access. Are you sure you want to delete this design system?`;
				displayConfirmModal({
					title: 'Delete design system',
					description: baselineMessage,
					submitLabel: 'Delete',
					onCancel: () => {},
					onSubmit: async () => {
						await forge.deleteDesignSystem(
							{
								designSystemId: designSystem.id,
							},
							true
						);
						// design system is gone! take user to the home page
						if (designSystem.id === activeDesignSystemId)
							navigate(routes.home(), { replace: true });
					},
				});
			}
		}),
	];

	const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (e.metaKey || e.ctrlKey) {
			onCommandClick && onCommandClick();
		} else if (onClick) {
			onClick();
		}
	};

	const onContextMenu = (e: React.MouseEvent) => {
		if (isMobile) return;
		dispatch(
			showContextMenu({
				point: { x: e.clientX + 5, y: e.clientY },
				menuItems: menuItems,
			})
		);
	};

	const handleContainerTap = () => {
		if (onClick) {
			onClick();
		}
	};

	return (
		<>
			<S.Base
				isActive={isActive}
				onClick={isMobile ? handleContainerTap : handleClick}
				onContextMenu={isMobile ? undefined : onContextMenu}
				title={designSystem.title}
				data-pressed={isPressed}
				{...touchHandlers}
			>
				<S.Title isActive={isActive}>{designSystem.title}</S.Title>
			</S.Base>
			{showShareModal && (
				<ShareDesignSystemModal
					designSystem={designSystem}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</>
	);
};

export default DesignSystemListItem;
